import React, { useMemo } from 'react';
import ReactHtmlParser from 'react-html-parser';
import {
  AccordionItem,
  Button,
  AFPTable,
  Alert,
} from '@gsa/afp-component-library';
import { useContractLine } from '../provider/contract-line-provider';
import {
  AccordionTitle,
  getHandelToggle,
  getToggleAccordion,
} from '../helpers/ec-components';
import {
  INPUT_TYPE_CODE,
  isMinReqItemReady,
  getUpdateObj,
  isNumericalMinimumComply,
} from '../helpers/ec-helpers';
import { getMinReqColumns, EcExpandedRow } from '../helpers/table-defs-ec';

const MinimumRequirements = () => {
  const {
    contractLine,
    standardECs,
    standardECTable: cats,
    setStandardECTable,
    setStandardEC,
    canUpdateContract,
  } = useContractLine();

  const catsWithNewEc = cats.filter((c) => c.hasNewEc).map((c) => c.title);

  const toggleAccordion = getToggleAccordion(setStandardECTable);
  const handleToggle = getHandelToggle(setStandardECTable);

  const readOnly = !canUpdateContract;

  const updateItem = async (original, updates) => {
    const updateObj = getUpdateObj(original, updates);
    const data = { ...updateObj };
    const newLine = { ...original, ...updateObj };
    data.ready = isMinReqItemReady(
      newLine.inputValue,
      newLine.comply,
      newLine.ecNotProvided || newLine.ecIsNew,
    );
    if (updates.comply === 'Y')
      data.inputException = { ...original.inputException, newValue: '' };
    const table =
      newLine.comply.newValue === 'N' ? { isExpanded: true } : undefined;
    setStandardEC(original, { data, table });
  };

  const onUpdate = (original, type, value) => {
    const { criteriaCode, lowerBound, upperBound } = original;
    let comply;
    const numVal = parseFloat(value);
    switch (type) {
      case 'comply':
        updateItem(original, { comply: value });
        break;
      case INPUT_TYPE_CODE.DROPDOWN:
        if (!value) comply = '';
        else if (original.preDefinedValue?.value.includes(value)) comply = 'Y';
        else comply = 'N';
        updateItem(original, { inputValue: value, comply });
        break;
      case INPUT_TYPE_CODE.COMPLY:
        if (!value) comply = '';
        else if (/^(comply|y)$/i.test(value)) comply = 'Y';
        else comply = 'N';
        updateItem(original, { inputValue: value, comply });
        break;
      case INPUT_TYPE_CODE.NUMERICAL:
        if (!value) comply = '';
        else if (Number.isNaN(numVal)) comply = 'N';
        else
          comply = isNumericalMinimumComply(numVal, criteriaCode, lowerBound)
            ? 'Y'
            : 'N';
        updateItem(original, { inputValue: value, comply });
        break;
      case INPUT_TYPE_CODE.RANGE:
        if (!value) comply = '';
        else if (Number.isNaN(numVal)) comply = 'N';
        else
          comply =
            (lowerBound == null || numVal >= lowerBound) &&
            (upperBound == null || numVal <= upperBound)
              ? 'Y'
              : 'N';
        updateItem(original, { inputValue: value, comply });
        break;
      case INPUT_TYPE_CODE.TEXT:
        updateItem(original, { inputValue: value });
        break;
      default:
        // case 'inputException' and 'inputExplanation':
        updateItem(original, { [type]: value });
    }
  };

  const columns = useMemo(
    () => getMinReqColumns(onUpdate, null, readOnly),
    [onUpdate],
    canUpdateContract,
  );

  const subrow = useMemo(
    () =>
      ({ row }) => (
        <EcExpandedRow
          ecType="S"
          row={row}
          onUpdate={onUpdate}
          isReadOnly={readOnly}
        />
      ),
    [onUpdate, readOnly],
  );

  return (
    <div className="margin-bottom-8 contract-line-template">
      <div className="title-m-bold">Minimum requirements</div>
      <div className="display-flex flex-row flex-justify flex-align-end margin-bottom-4">
        <div
          style={{
            maxWidth: 760,
            maxHeight: 160,
            overflow: 'auto',
            paddingRight: 8,
          }}
        >
          {/* Using ReactHtmlParser as it is safer than dangerouslySetInnerHTML */}
          {contractLine.standardItem.content?.content
            ? ReactHtmlParser(contractLine.standardItem.content?.content)
            : ''}
        </div>
        <div>
          <Button
            variant="unstyled"
            label="Open/close all"
            leftIcon={{ name: 'unfold_more' }}
            onClick={toggleAccordion}
          />
        </div>
      </div>

      {catsWithNewEc.length > 0 && (
        <div className="margin-bottom-4">
          <Alert type="warning">
            New equipment codes have been added to the following categories:{' '}
            <span className="text-bold">{catsWithNewEc.join(', ')}</span>.
          </Alert>
        </div>
      )}

      {cats.map((cat, i) => {
        const isReady = standardECs[cat.index].data.every((row) => row.ready);
        return (
          <AccordionItem
            key={cat.id}
            id={cat.id}
            title={
              <AccordionTitle
                title={cat.title}
                isReady={isReady}
                hasNewEc={cat.hasNewEc}
              />
            }
            expanded={cat.expanded}
            content={
              cat.expanded && (
                <div style={{ paddingTop: 8 }}>
                  <AFPTable
                    fullWidth
                    columns={columns}
                    data={cat.data}
                    renderRowSubComponent={subrow}
                  />
                </div>
              )
            }
            handleToggle={() => handleToggle(i)}
          />
        );
      })}
    </div>
  );
};

export default MinimumRequirements;
